import { useContext, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { MdRoute } from "react-icons/md"
import { BtnRedondo } from "./btnRedondo"
import { ContextoGeral } from "../contextos/ContextoGeral"
import { dominioTemMinhaRota, lerMinhaRotaVisivel } from "../utils/preferenciaMinhaRota"

const STORAGE = "fab-minha-rota-pos"
const TAM = 70
const MARGEM = 8

function posPadrao() {
	if (typeof window === "undefined") return { right: true, y: 400 }
	return {
		right: true,
		y: Math.max(88, window.innerHeight - 168),
	}
}

function lerPos() {
	try {
		const v = JSON.parse(localStorage.getItem(STORAGE) || "null")
		if (v && typeof v.y === "number") return { right: v.right !== false, y: v.y }
	} catch { /* ignore */ }
	return null
}

function salvarPos(pos) {
	try { localStorage.setItem(STORAGE, JSON.stringify(pos)) } catch { /* ignore */ }
}

function limitarY(y) {
	const top = 72
	const max = window.innerHeight - TAM - 12
	return Math.min(max, Math.max(top, y))
}

export default function FabMinhaRota({ badge, visible = true }) {
	const navegar = useNavigate()
	const { dados } = useContext(ContextoGeral)
	const n = Number(badge) || 0
	const [pos, setPos] = useState(() => lerPos() || posPadrao())
	const [livre, setLivre] = useState(null)
	const arrasto = useRef(null)
	const moved = useRef(false)
	const posRef = useRef(pos)
	posRef.current = pos

	useEffect(() => {
		function enquadrar() {
			setPos((atual) => {
				const prox = { ...atual, y: limitarY(atual.y) }
				salvarPos(prox)
				return prox
			})
		}
		window.addEventListener("resize", enquadrar)
		return () => window.removeEventListener("resize", enquadrar)
	}, [])

	if (!visible || !dominioTemMinhaRota(dados?.dominio) || !lerMinhaRotaVisivel(dados?.idProf) || n <= 0 || typeof document === "undefined") return null

	function onPointerDown(e) {
		e.currentTarget.setPointerCapture?.(e.pointerId)
		arrasto.current = {
			x: e.clientX,
			y: e.clientY,
			top: posRef.current.y,
			left: posRef.current.right ? window.innerWidth - TAM - MARGEM : MARGEM,
		}
		moved.current = false
	}

	function onPointerMove(e) {
		if (!arrasto.current) return
		const dx = e.clientX - arrasto.current.x
		const dy = e.clientY - arrasto.current.y
		if (Math.hypot(dx, dy) > 10) moved.current = true
		if (!moved.current) return
		setLivre({
			x: Math.min(window.innerWidth - TAM - 4, Math.max(4, arrasto.current.left + dx)),
			y: limitarY(arrasto.current.top + dy),
		})
	}

	function onPointerUp() {
		const arrastou = moved.current
		arrasto.current = null
		if (!arrastou) return
		setLivre((atual) => {
			const y = limitarY(atual?.y ?? posRef.current.y)
			const centro = (atual?.x ?? 0) + TAM / 2
			const prox = { right: centro >= window.innerWidth / 2, y }
			salvarPos(prox)
			setPos(prox)
			return null
		})
	}

	const estilo = livre
		? { left: livre.x, top: livre.y, right: "auto" }
		: {
			top: pos.y,
			right: pos.right ? MARGEM : "auto",
			left: pos.right ? "auto" : MARGEM,
		}

	return createPortal(
		<div
			className="fab-minha-rota"
			style={{
				position: "fixed",
				zIndex: 1300,
				touchAction: "none",
				userSelect: "none",
				cursor: livre ? "grabbing" : "grab",
				...estilo,
			}}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerCancel={onPointerUp}
		>
			<BtnRedondo
				badge={n}
				collapse
				style={{ backgroundColor: "var(--primaria)", color: "#fff", margin: 0 }}
				onClick={(e) => {
					e.preventDefault()
					e.stopPropagation()
					if (moved.current) return
					navegar("/app/rotaServicos")
				}}
			>
				<MdRoute aria-hidden />
			</BtnRedondo>
		</div>,
		document.body
	)
}
